// OperatorsPage — the operator registry, surfaced in "Connect & Scope"
// right after Columns (Huayin 2026-06-05).
//
// The L1 *system operator catalog* (Manual ch.10) is a site-wide property
// of the authoring context: the same catalog every AC over this data
// source starts from. This page displays it — "content taken from the
// manual" — and gives the authoring process a chance to override two
// presentation/behaviour properties per operator:
//
//   · display name    — what the operator is called in this AC's surfaces
//                        (the catalog name stays the immutable key).
//   · missing behavior — pin the missing-value treatment. This is NOT a
//                        single scalar (Huayin 2026-06-05): per Manual §10.2
//                        + §10.12 it is a *map* from input missingness
//                        category (MCAR / MAR / MNAR) to a behavior drawn
//                        from the v4 set (skip / skip+rescale / stratified /
//                        propagate / fill / refuse — ADR-012; `impute` was
//                        split into `fill` + the rescale estimators). The
//                        author pins the per-category cells that should
//                        deviate from the catalog default.
//
// Overrides persist on the session via the SetOperatorRegistry op and are
// frozen at COMMITTED alongside the rest of scope (SELECTING-only), since
// the missing-value semantics feed the integrity attestations. Distinct
// from the per-AC physical-binding overrides (Declare/legacy, W4 §3.6),
// which map a catalog name to a physical operator name.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ApiError,
  type MissingBehavior,
  type MissingBehaviorMap,
  type MissingCategory,
  type OperatorInfo,
} from "../api/client";
import { useSession } from "./SessionContext";

// The input missingness categories an operator dispatches on (Manual §10.2).
const CATEGORIES: readonly MissingCategory[] = ["MCAR", "MAR", "MNAR"];

// The behaviors an author may pin per category — the full v4 set
// (`MISSING_BEHAVIORS` in coframe-core, ADR-012). Must include every token
// that can appear as a catalog DEFAULT (extensive MCAR → skip+rescale, MAR →
// stratified, MNAR → refuse) so the select can render the default, plus
// `fill` as a legitimate author override (its constant lives on the
// column-spec). skip = the manual's "exclude".
const BEHAVIORS: readonly MissingBehavior[] = [
  "skip",
  "skip+rescale",
  "stratified",
  "propagate",
  "fill",
  "refuse",
];

const CATEGORY_HINT: Record<MissingCategory, string> = {
  MCAR: "Missing completely at random — missingness unrelated to anything.",
  MAR: "Missing at random — missingness depends on an observed coordinate.",
  MNAR: "Missing not at random — missingness depends on the value itself.",
};

interface DraftOverride {
  display_name: string; // "" = no override
  // The effective per-category selection. An override is any cell that
  // differs from the operator's catalog default.
  missing: MissingBehaviorMap;
}

export function OperatorsPage() {
  const { client, session, refreshSession } = useSession();

  const [operators, setOperators] = useState<OperatorInfo[] | null>(null);
  // Local edit buffer keyed by canonical operator name. Seeded from the
  // server's merged registry on load; "Save" round-trips the whole map.
  const [draft, setDraft] = useState<Map<string, DraftOverride>>(new Map());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  const committed = session?.scope_phase === "committed";
  const readOnly = committed;

  const load = useCallback(async () => {
    if (session === null) return;
    setLoading(true);
    setError(null);
    try {
      const r = await client.getSessionOperators(session.session_id);
      setOperators(r.operators);
      setDraft(seedDraft(r.operators));
    } catch (e: unknown) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [client, session]);

  useEffect(() => {
    void load();
  }, [load]);

  // Has the buffer diverged from the server's current effective state?
  const dirty = useMemo(() => {
    if (operators === null) return false;
    return operators.some((op) => {
      const d = draft.get(op.name);
      if (!d) return false;
      const curName = op.name_overridden ? op.display_name : "";
      if (d.display_name !== curName) return true;
      return CATEGORIES.some((c) => d.missing[c] !== op.missing_behavior[c]);
    });
  }, [operators, draft]);

  const overrideCount = useMemo(() => {
    if (operators === null) return 0;
    let n = 0;
    for (const op of operators) {
      const d = draft.get(op.name);
      if (!d) continue;
      const nameOver = Boolean(d.display_name.trim()) && d.display_name.trim() !== op.name;
      const missingOver = CATEGORIES.some(
        (c) => d.missing[c] !== op.default_missing_behavior[c],
      );
      if (nameOver || missingOver) n += 1;
    }
    return n;
  }, [operators, draft]);

  const patchName = useCallback((name: string, display_name: string) => {
    setSavedMessage(null);
    setDraft((prev) => {
      const next = new Map(prev);
      const cur = next.get(name);
      if (cur) next.set(name, { ...cur, display_name });
      return next;
    });
  }, []);

  const patchMissing = useCallback(
    (name: string, category: MissingCategory, behavior: MissingBehavior) => {
      setSavedMessage(null);
      setDraft((prev) => {
        const next = new Map(prev);
        const cur = next.get(name);
        if (cur) {
          next.set(name, {
            ...cur,
            missing: { ...cur.missing, [category]: behavior },
          });
        }
        return next;
      });
    },
    [],
  );

  const save = useCallback(async () => {
    if (session === null || operators === null) return;
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    // Build the override patch — only the cells that deviate from the
    // catalog default. missing_behavior is a {category: behavior} map.
    const overrides: Record<
      string,
      { display_name?: string; missing_behavior?: Record<string, string> }
    > = {};
    for (const op of operators) {
      const d = draft.get(op.name);
      if (!d) continue;
      const entry: { display_name?: string; missing_behavior?: Record<string, string> } = {};
      const dn = d.display_name.trim();
      if (dn && dn !== op.name) entry.display_name = dn;
      const missing: Record<string, string> = {};
      for (const c of CATEGORIES) {
        if (d.missing[c] && d.missing[c] !== op.default_missing_behavior[c]) {
          missing[c] = d.missing[c];
        }
      }
      if (Object.keys(missing).length > 0) entry.missing_behavior = missing;
      if (Object.keys(entry).length > 0) overrides[op.name] = entry;
    }
    try {
      const r = await client.runOp(session.session_id, "SetOperatorRegistry", {
        inputs: { overrides },
      });
      if (!r.success) {
        setError(r.error ?? "SetOperatorRegistry failed.");
        return;
      }
      await refreshSession();
      await load();
      const n = (r.result?.override_count as number | undefined) ?? overrideCount;
      setSavedMessage(`Saved — ${n} operator${n === 1 ? "" : "s"} overridden.`);
    } catch (e: unknown) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  }, [client, session, operators, draft, overrideCount, refreshSession, load]);

  const reset = useCallback(() => {
    if (operators) setDraft(seedDraft(operators));
    setSavedMessage(null);
  }, [operators]);

  if (session === null) {
    return (
      <div>
        <h2 style={headingStyle}>Operators</h2>
        <p style={mutedStyle}>Start a Lattice first to see its operator registry.</p>
      </div>
    );
  }

  const reducers = (operators ?? []).filter((o) => o.kind === "reducer");
  const functions = (operators ?? []).filter((o) => o.kind === "function");

  return (
    <div>
      <h2 style={headingStyle}>Operators</h2>
      <p style={mutedStyle}>
        The L1 system operator catalog (Manual ch.10) — a site-wide property of
        this data source. Every operator here is available to declarations
        downstream. You can give an operator a different <em>display name</em>{" "}
        and pin its <em>missing-value behavior</em> per missingness category
        (MCAR / MAR / MNAR); everything else inherits the system default.
      </p>

      {committed && (
        <div style={lockBannerStyle}>
          Scope is <strong>committed</strong> — the operator registry is frozen.
          Fork the Lattice to change overrides.
        </div>
      )}

      <div style={toolbarStyle}>
        <button
          onClick={save}
          disabled={readOnly || saving || !dirty}
          style={primaryBtnStyle(readOnly || saving || !dirty)}
        >
          {saving ? "Saving…" : "Save overrides"}
        </button>
        <button
          onClick={reset}
          disabled={readOnly || saving || !dirty}
          style={secondaryBtnStyle(readOnly || saving || !dirty)}
        >
          Reset
        </button>
        <span style={mutedStyle}>
          {overrideCount} operator{overrideCount === 1 ? "" : "s"} overridden
          {dirty ? " · unsaved changes" : ""}
        </span>
        <button onClick={() => void load()} disabled={loading} style={linkBtnStyle}>
          {loading ? "Loading…" : "Reload"}
        </button>
      </div>

      {error && <div style={errorStyle}>{error}</div>}
      {savedMessage && <div style={savedStyle}>{savedMessage}</div>}

      {operators === null ? (
        <p style={mutedStyle}>Loading operators…</p>
      ) : (
        <>
          <OperatorTable
            title="Reducers"
            subtitle="Aggregate many rows to one value (Manual §10.4). Missing-value behavior is per (operator, missingness category)."
            operators={reducers}
            draft={draft}
            readOnly={readOnly}
            onPatchName={patchName}
            onPatchMissing={patchMissing}
          />
          <OperatorTable
            title="Functions"
            subtitle="Row-wise mappers / FD-edge generators (Manual §10.5). A missing input propagates as null — no reducer missing-value semantics to pin."
            operators={functions}
            draft={draft}
            readOnly={readOnly}
            onPatchName={patchName}
            onPatchMissing={patchMissing}
          />
        </>
      )}
    </div>
  );
}

// ── Operator table (one per kind) ───────────────────────────────────────

function OperatorTable({
  title,
  subtitle,
  operators,
  draft,
  readOnly,
  onPatchName,
  onPatchMissing,
}: {
  title: string;
  subtitle: string;
  operators: OperatorInfo[];
  draft: Map<string, DraftOverride>;
  readOnly: boolean;
  onPatchName: (name: string, display_name: string) => void;
  onPatchMissing: (
    name: string,
    category: MissingCategory,
    behavior: MissingBehavior,
  ) => void;
}) {
  if (operators.length === 0) return null;
  return (
    <section style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <span style={sectionTitleStyle}>{title}</span>
        <span style={sectionMetaStyle}>
          {operators.length} operator{operators.length === 1 ? "" : "s"}
        </span>
      </div>
      <p style={{ ...mutedStyle, margin: "0 0 0.6rem" }}>{subtitle}</p>
      <div style={gridWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Operator</th>
              <th style={thStyle}>Display name</th>
              <th style={thStyle} title="Partition-invariant (Manual §10)">
                Part-inv
              </th>
              <th style={thStyle} title="Identity-preserving">
                Id-pres
              </th>
              <th style={thStyle} title="Value scales with input size (§10.12.2)">
                Extensive
              </th>
              <th
                style={thStyle}
                title="Result depends on input order — scans / ordered reducers (§5.5). Refused until a determinable order is supplied."
              >
                Order-dep
              </th>
              <th style={thStyle} title="Missing-value treatment per category (§10.2 + §10.12)">
                Missing behavior (by category)
              </th>
              <th style={thStyle}>Description</th>
            </tr>
          </thead>
          <tbody>
            {operators.map((op) => {
              const d = draft.get(op.name) ?? {
                display_name: "",
                missing: { ...op.missing_behavior },
              };
              return (
                <tr key={op.name}>
                  <td style={tdStyle}>
                    <code style={opNameStyle}>{op.name}</code>
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={d.display_name}
                      placeholder={op.name}
                      disabled={readOnly}
                      onChange={(e) => onPatchName(op.name, e.target.value)}
                      style={inputStyle(readOnly)}
                    />
                  </td>
                  <td style={tdCenterStyle}>{op.partition_invariant ? "✓" : "—"}</td>
                  <td style={tdCenterStyle}>{op.identity_preserving ? "✓" : "—"}</td>
                  <td style={tdCenterStyle}>{op.is_extensive ? "✓" : "—"}</td>
                  <td style={tdCenterStyle}>{op.order_dependent ? "✓" : "—"}</td>
                  <td style={tdStyle}>
                    {op.missing_editable ? (
                      <MissingBehaviorEditor
                        op={op}
                        draft={d}
                        readOnly={readOnly}
                        onPatch={(c, b) => onPatchMissing(op.name, c, b)}
                      />
                    ) : (
                      <span style={mutedStyle} title="Functions propagate a null input as null.">
                        propagate (null)
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: "#555" }}>{op.description}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// One {category → behavior} editor: three labeled selects, one per
// missingness category. A cell that deviates from the catalog default is
// flagged so the author can see what they've changed.
function MissingBehaviorEditor({
  op,
  draft,
  readOnly,
  onPatch,
}: {
  op: OperatorInfo;
  draft: DraftOverride;
  readOnly: boolean;
  onPatch: (category: MissingCategory, behavior: MissingBehavior) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {CATEGORIES.map((c) => {
        const value = draft.missing[c] ?? op.default_missing_behavior[c];
        const isOverride = value !== op.default_missing_behavior[c];
        return (
          <div key={c} style={catRowStyle}>
            <span style={catLabelStyle} title={CATEGORY_HINT[c]}>
              {c}
            </span>
            <select
              value={value}
              disabled={readOnly}
              onChange={(e) => onPatch(c, e.target.value as MissingBehavior)}
              style={selectStyle(readOnly, isOverride)}
            >
              {BEHAVIORS.map((b) => (
                <option key={b} value={b}>
                  {b === op.default_missing_behavior[c] ? `${b} (default)` : b}
                </option>
              ))}
            </select>
          </div>
        );
      })}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function seedDraft(operators: OperatorInfo[]): Map<string, DraftOverride> {
  const m = new Map<string, DraftOverride>();
  for (const op of operators) {
    m.set(op.name, {
      display_name: op.name_overridden ? op.display_name : "",
      // Seed from the effective (default ⊕ override) per-category map.
      missing: { ...op.missing_behavior },
    });
  }
  return m;
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── Styles ──────────────────────────────────────────────────────────────

const headingStyle: React.CSSProperties = { marginBottom: "0.25rem" };
const mutedStyle: React.CSSProperties = { color: "#666", fontSize: "0.85rem" };
const sectionStyle: React.CSSProperties = { marginTop: "1.5rem" };
const sectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "0.5rem",
};
const sectionTitleStyle: React.CSSProperties = {
  fontWeight: 600,
  color: "#1c4e80",
  fontSize: "1rem",
};
const sectionMetaStyle: React.CSSProperties = {
  fontSize: "0.78rem",
  color: "#64748b",
  fontVariantNumeric: "tabular-nums",
};
const gridWrapStyle: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  overflow: "auto",
};
const tableStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  fontSize: "0.85rem",
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.5rem 0.6rem",
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#475569",
  fontWeight: 600,
  whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
  padding: "0.4rem 0.6rem",
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};
const tdCenterStyle: React.CSSProperties = { ...tdStyle, textAlign: "center" };
const opNameStyle: React.CSSProperties = {
  fontWeight: 600,
  color: "#0f172a",
};
const catRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
};
const catLabelStyle: React.CSSProperties = {
  display: "inline-block",
  width: "3rem",
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#64748b",
  letterSpacing: "0.02em",
  cursor: "help",
};
const inputStyle = (disabled: boolean): React.CSSProperties => ({
  width: "9rem",
  padding: "0.25rem 0.4rem",
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  fontSize: "0.85rem",
  background: disabled ? "#f1f5f9" : "#fff",
  color: disabled ? "#94a3b8" : "#0f172a",
});
const selectStyle = (disabled: boolean, override: boolean): React.CSSProperties => ({
  padding: "0.2rem 0.35rem",
  border: `1px solid ${override ? "#0366d6" : "#cbd5e1"}`,
  borderRadius: 4,
  fontSize: "0.82rem",
  fontWeight: override ? 600 : 400,
  background: disabled ? "#f1f5f9" : override ? "#eef4ff" : "#fff",
  color: disabled ? "#94a3b8" : "#0f172a",
});
const toolbarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  margin: "0.75rem 0",
};
const primaryBtnStyle = (disabled: boolean): React.CSSProperties => ({
  padding: "0.4rem 0.9rem",
  background: disabled ? "#cbd5e1" : "#0366d6",
  color: "#fff",
  border: "none",
  borderRadius: 5,
  fontSize: "0.85rem",
  fontWeight: 500,
  cursor: disabled ? "not-allowed" : "pointer",
});
const secondaryBtnStyle = (disabled: boolean): React.CSSProperties => ({
  padding: "0.4rem 0.9rem",
  background: "#fff",
  color: disabled ? "#94a3b8" : "#334155",
  border: "1px solid #cbd5e1",
  borderRadius: 5,
  fontSize: "0.85rem",
  cursor: disabled ? "not-allowed" : "pointer",
});
const linkBtnStyle: React.CSSProperties = {
  marginLeft: "auto",
  background: "none",
  border: "none",
  color: "#0366d6",
  fontSize: "0.82rem",
  cursor: "pointer",
};
const errorStyle: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  background: "#fdecea",
  border: "1px solid #f5c2c0",
  borderRadius: 5,
  color: "#a12622",
  fontSize: "0.85rem",
  margin: "0.5rem 0",
};
const savedStyle: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  background: "#e7f6ec",
  border: "1px solid #aedcbb",
  borderRadius: 5,
  color: "#1a6b3a",
  fontSize: "0.85rem",
  margin: "0.5rem 0",
};
const lockBannerStyle: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  background: "#fff5d6",
  border: "1px solid #e6d8a8",
  borderRadius: 5,
  color: "#8b6914",
  fontSize: "0.85rem",
  margin: "0.5rem 0",
};
